import { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { CaretLeft, CaretRight, Camera, CalendarX, Plus, Trash, Clock, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths,
  startOfWeek, endOfWeek, isSameMonth, isBefore, startOfDay,
} from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";

const iso = (d) => format(d, "yyyy-MM-dd");
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function PhotographerCalendar() {
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ shoots: [], off_dates: [], time_off: [], company_dates: [], recurring_off_dates: [], recurring_time_off: [], recurring: { weekdays: [], blocks: [] } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selDate, setSelDate] = useState(null); // Date whose editor dialog is open
  const [nStart, setNStart] = useState("09:00");
  const [nEnd, setNEnd] = useState("12:00");
  // Recurring weekly schedule editor
  const [recurOpen, setRecurOpen] = useState(false);
  const [recWeekdays, setRecWeekdays] = useState([]);
  const [recBlocks, setRecBlocks] = useState([]);
  const [rbWeekday, setRbWeekday] = useState(1);
  const [rbStart, setRbStart] = useState("12:00");
  const [rbEnd, setRbEnd] = useState("13:00");
  const [recSaving, setRecSaving] = useState(false);
  const today = startOfDay(new Date());

  const load = async (base) => {
    setLoading(true);
    try {
      const { data } = await client.get("/booking/my-calendar", { params: { month: format(base, "yyyy-MM") } });
      setData(data);
    } catch { toast.error("Couldn't load your calendar."); }
    setLoading(false);
  };
  useEffect(() => { load(cursor); /* eslint-disable-next-line */ }, [cursor]);

  const days = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)),
  }), [cursor]);

  const shootsBy = useMemo(() => {
    const m = {};
    (data.shoots || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);
  const timeOffBy = useMemo(() => {
    const m = {};
    (data.time_off || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);
  const offSet = useMemo(() => new Set(data.off_dates || []), [data]);
  const recurringOffSet = useMemo(() => new Set(data.recurring_off_dates || []), [data]);
  const recurringBlocksBy = useMemo(() => {
    const m = {};
    (data.recurring_time_off || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);
  const companyMap = useMemo(() => Object.fromEntries((data.company_dates || []).map((c) => [c.date, c.reason])), [data]);

  const key = selDate ? iso(selDate) : null;
  const selShoots = key ? (shootsBy[key] || []) : [];
  const selOneOffBlocks = key ? (timeOffBy[key] || []) : [];
  const selRecurBlocks = key ? (recurringBlocksBy[key] || []) : [];
  const selOff = key ? offSet.has(key) : false;
  const selRecurOff = key ? recurringOffSet.has(key) : false;

  const openDay = (d) => {
    const k = iso(d);
    if (companyMap[k]) { toast.info(`Company holiday: ${companyMap[k]}`); return; }
    if (isBefore(d, today)) return;
    setNStart("09:00"); setNEnd("12:00");
    setSelDate(d);
  };

  // ---- Recurring weekly schedule ----
  const openRecur = () => {
    setRecWeekdays([...(data.recurring?.weekdays || [])]);
    setRecBlocks([...(data.recurring?.blocks || [])]);
    setRbWeekday(1); setRbStart("12:00"); setRbEnd("13:00");
    setRecurOpen(true);
  };
  const toggleRecWeekday = (wd) => {
    setRecWeekdays((prev) => prev.includes(wd) ? prev.filter((x) => x !== wd) : [...prev, wd].sort());
  };
  const addRecBlock = () => {
    if (rbEnd <= rbStart) { toast.error("End time must be after start time."); return; }
    setRecBlocks((prev) => [...prev, { id: `tmp-${Date.now()}`, weekday: rbWeekday, start: rbStart, end: rbEnd }]);
  };
  const removeRecBlock = (id) => setRecBlocks((prev) => prev.filter((b) => b.id !== id));
  const saveRecurring = async () => {
    setRecSaving(true);
    try {
      await client.put("/booking/my-recurring", { weekdays: recWeekdays, blocks: recBlocks });
      toast.success("Weekly schedule saved — it now fills your calendar automatically.");
      setRecurOpen(false);
      await load(cursor);
    } catch { toast.error("Couldn't save your weekly schedule."); }
    setRecSaving(false);
  };

  const markAllDay = async (on) => {
    if (selShoots.length && on) { toast.error("You have a shoot that day — can't mark it fully off."); return; }
    setSaving(true);
    try {
      if (on) await client.post("/booking/my-days-off", { date: key });
      else await client.delete(`/booking/my-days-off/${key}`);
      await load(cursor);
    } catch { toast.error("Couldn't update that day."); }
    setSaving(false);
  };

  const addBlock = async () => {
    if (nEnd <= nStart) { toast.error("End time must be after start time."); return; }
    setSaving(true);
    try {
      await client.post("/booking/my-time-off", { date: key, start: nStart, end: nEnd });
      toast.success(`Blocked ${fmtTime(nStart)}–${fmtTime(nEnd)}`);
      await load(cursor);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add that time block."); }
    setSaving(false);
  };

  const removeBlock = async (id) => {
    setSaving(true);
    try {
      await client.delete(`/booking/my-time-off/${id}`);
      await load(cursor);
    } catch { toast.error("Couldn't remove that block."); }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="photog-calendar">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#B08D57] font-semibold mb-1">Scheduling</p>
          <h2 className="text-2xl font-bold text-[#1A1715]">My Calendar</h2>
          <p className="text-sm text-[#1A1715]/60 mt-1">You're available every day by default. Set a <strong>weekly schedule</strong> for recurring days/hours off, or tap any day for one-off changes. It updates your booking page instantly.</p>
        </div>
        <Button data-testid="open-weekly-schedule" onClick={openRecur} variant="outline" className="border-[#B08D57]/50 text-[#8A6D1B] hover:bg-[#B08D57]/10 shrink-0">
          <ArrowsClockwise size={16} weight="bold" className="mr-1.5" />Weekly schedule
        </Button>
      </div>

      <div className="flex items-center justify-between">
        <button data-testid="cal-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="p-2 rounded-md hover:bg-[#1A1715]/5"><CaretLeft size={18} /></button>
        <h3 className="text-lg font-semibold text-[#1A1715]" data-testid="cal-month">{format(cursor, "MMMM yyyy")}</h3>
        <button data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))} className="p-2 rounded-md hover:bg-[#1A1715]/5"><CaretRight size={18} /></button>
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-[#1A1715]/60">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#7E1119]" /> Shoot</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-[#1A1715]/40 bg-[#1A1715]/10" /> Day off</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#B08D57]/60 border border-[#B08D57]" /> Hours blocked</span>
        <span className="flex items-center gap-1.5"><ArrowsClockwise size={12} weight="bold" className="text-[#8A6D1B]" /> Recurring (weekly)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#B08D57]" /> Company holiday</span>
      </div>

      <div className="bg-white border border-[#1A1715]/12 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-wider text-[#1A1715]/50 border-b border-[#1A1715]/10">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className={`grid grid-cols-7 ${loading ? "opacity-40 pointer-events-none" : ""}`}>
          {days.map((d) => {
            const k = iso(d);
            const inMonth = isSameMonth(d, cursor);
            const past = isBefore(d, today);
            const shoots = shootsBy[k] || [];
            const oneOff = timeOffBy[k] || [];
            const recurBlk = recurringBlocksBy[k] || [];
            const blocks = [...oneOff, ...recurBlk];
            const recurOff = recurringOffSet.has(k);
            const isOff = offSet.has(k) || recurOff;
            const holiday = companyMap[k];
            const base = "min-h-[74px] border-b border-r border-[#1A1715]/8 p-1.5 text-left align-top transition-colors relative";
            let bg = "bg-white hover:bg-[#F1EDE4]";
            if (holiday) bg = "bg-[#B08D57]/15";
            else if (isOff) bg = "bg-[#1A1715]/8";
            else if (blocks.length) bg = "bg-[#B08D57]/[0.08]";
            if (!inMonth) bg += " opacity-40";
            if (past && !shoots.length) bg += " opacity-50";
            return (
              <button key={k} data-testid={`cal-day-${k}`} onClick={() => openDay(d)}
                disabled={saving}
                className={`${base} ${bg}`}>
                <span className={`text-xs font-semibold ${holiday ? "text-[#7E1119]" : "text-[#1A1715]"}`}>{format(d, "d")}</span>
                {recurOff && !holiday && <ArrowsClockwise size={10} weight="bold" className="absolute top-1.5 right-1.5 text-[#8A6D1B]" />}
                {holiday && <span className="block text-[9px] text-[#7E1119] font-medium leading-tight mt-0.5 truncate" title={holiday}>{holiday}</span>}
                {isOff && !holiday && <span className="block text-[9px] text-[#1A1715]/60 font-medium mt-0.5">{recurOff && !offSet.has(k) ? "Off (weekly)" : "Off all day"}</span>}
                {!isOff && !holiday && blocks.slice(0, 1).map((b, i) => (
                  <span key={b.id || i} className="flex items-center gap-0.5 text-[9px] text-[#8A6D1B] font-medium leading-tight mt-0.5 truncate">{b.recurring && <ArrowsClockwise size={8} weight="bold" className="flex-none" />}{fmtTime(b.start)}–{fmtTime(b.end)}</span>
                ))}
                {!isOff && !holiday && blocks.length > 1 && <span className="block text-[9px] text-[#8A6D1B] font-semibold">+{blocks.length - 1} block{blocks.length - 1 > 1 ? "s" : ""}</span>}
                <div className="mt-1 space-y-0.5">
                  {shoots.slice(0, 2).map((s, i) => (
                    <span key={i} className="flex items-center gap-1 text-[9px] text-white bg-[#7E1119] rounded px-1 py-0.5 truncate" title={s.address}>
                      <Camera size={9} weight="fill" className="flex-none" />{s.time || "Shoot"}
                    </span>
                  ))}
                  {shoots.length > 2 && <span className="text-[9px] text-[#7E1119] font-semibold">+{shoots.length - 2} more</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-[#1A1715]/50 flex items-center gap-1.5"><CalendarX size={13} /> Company holidays are set by your admin and can't be booked by anyone.</p>

      <Dialog open={!!selDate} onOpenChange={(o) => !o && setSelDate(null)}>
        <DialogContent className="max-w-md bg-[#FBFAF7]" data-testid="day-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#4A0000]">{selDate ? format(selDate, "EEEE, MMM d") : ""}</DialogTitle>
            <DialogDescription>Mark yourself off for the whole day or block specific hours. This updates your booking page instantly.</DialogDescription>
          </DialogHeader>

          {selShoots.length > 0 && (
            <div className="rounded-[5px] bg-[#7E1119]/5 border border-[#7E1119]/15 p-3 space-y-1" data-testid="day-editor-shoots">
              <p className="text-xs uppercase tracking-wider text-[#7E1119] font-semibold">Scheduled shoots</p>
              {selShoots.map((s, i) => (
                <p key={i} className="text-sm text-[#1A1715] flex items-center gap-1.5"><Camera size={13} weight="fill" className="text-[#7E1119]" />{s.time || "Time TBD"} — {s.address}</p>
              ))}
            </div>
          )}

          {selRecurOff ? (
            <div className="rounded-[5px] border border-[#B08D57]/30 bg-[#B08D57]/10 px-4 py-3 flex items-start gap-2" data-testid="day-recurring-off-note">
              <ArrowsClockwise size={16} weight="bold" className="text-[#8A6D1B] mt-0.5 flex-none" />
              <div>
                <p className="font-semibold text-[#8A6D1B]">Off every {selDate ? WEEKDAYS_LONG[selDate.getDay()] : ""}</p>
                <p className="text-xs text-[#5C5A58]">Set by your weekly schedule. <button onClick={() => { setSelDate(null); openRecur(); }} className="underline font-medium">Edit weekly schedule</button></p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-[5px] border border-[#E9DDC6] bg-white px-4 py-3">
              <div>
                <p className="font-semibold text-[#1A1715]">Unavailable all day</p>
                <p className="text-xs text-[#5C5A58]">{selShoots.length ? "You have a shoot this day." : "Block the entire day."}</p>
              </div>
              <Switch data-testid="day-off-switch" checked={selOff} disabled={saving || (selShoots.length > 0 && !selOff)} onCheckedChange={markAllDay} />
            </div>
          )}

          {!selOff && !selRecurOff && (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wider text-[#8A6D1B] font-semibold flex items-center gap-1.5"><Clock size={13} weight="bold" />Unavailable hours</p>
              {selRecurBlocks.map((b, i) => (
                <div key={`r-${i}`} data-testid={`recur-block-${i}`} className="flex items-center justify-between rounded-[5px] bg-[#B08D57]/10 border border-[#B08D57]/25 px-3 py-2">
                  <span className="text-sm font-medium text-[#8A6D1B] flex items-center gap-1.5"><ArrowsClockwise size={13} weight="bold" />{fmtTime(b.start)} – {fmtTime(b.end)}<span className="text-[9px] uppercase tracking-wide bg-[#B08D57]/25 rounded px-1 py-0.5">weekly</span></span>
                  <button onClick={() => { setSelDate(null); openRecur(); }} className="text-[#8A6D1B] hover:text-[#7E1119] text-xs underline">Edit</button>
                </div>
              ))}
              {selOneOffBlocks.length > 0 ? (
                <div className="space-y-2">
                  {selOneOffBlocks.map((b) => (
                    <div key={b.id} data-testid={`timeoff-item-${b.id}`} className="flex items-center justify-between rounded-[5px] bg-[#B08D57]/10 border border-[#B08D57]/25 px-3 py-2">
                      <span className="text-sm font-medium text-[#8A6D1B]">{fmtTime(b.start)} – {fmtTime(b.end)}</span>
                      <button data-testid={`remove-timeoff-${b.id}`} onClick={() => removeBlock(b.id)} disabled={saving} className="text-[#8A6D1B] hover:text-[#7E1119] p-1"><Trash size={15} weight="bold" /></button>
                    </div>
                  ))}
                </div>
              ) : selRecurBlocks.length === 0 ? (
                <p className="text-sm text-[#5C5A58]">No blocked hours — you're available all day.</p>
              ) : null}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-[#5C5A58] mb-1">From</label>
                  <input data-testid="add-timeoff-start" type="time" step="900" value={nStart} onChange={(e) => setNStart(e.target.value)} className="w-full rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-1.5 text-sm" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-[#5C5A58] mb-1">To</label>
                  <input data-testid="add-timeoff-end" type="time" step="900" value={nEnd} onChange={(e) => setNEnd(e.target.value)} className="w-full rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-1.5 text-sm" />
                </div>
                <Button data-testid="add-timeoff-btn" onClick={addBlock} disabled={saving} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0"><Plus size={15} weight="bold" className="mr-1" />Add</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recurring weekly schedule editor */}
      <Dialog open={recurOpen} onOpenChange={(o) => !o && !recSaving && setRecurOpen(false)}>
        <DialogContent className="max-w-lg bg-[#FBFAF7]" data-testid="weekly-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#4A0000] flex items-center gap-2"><ArrowsClockwise size={22} weight="bold" />Weekly schedule</DialogTitle>
            <DialogDescription>Set the days and hours you're regularly off. These repeat every week and fill your calendar and booking page automatically. One-off changes on any day still override this.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-[#8A6D1B] font-semibold">Days off every week</p>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((lbl, wd) => (
                <button key={wd} data-testid={`recur-weekday-${wd}`} onClick={() => toggleRecWeekday(wd)}
                  className={`w-11 h-11 rounded-full text-sm font-semibold border transition-colors ${recWeekdays.includes(wd) ? "bg-[#4A0000] text-white border-[#4A0000]" : "bg-white text-[#1A1715] border-[#E9DDC6] hover:border-[#B08D57]"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wider text-[#8A6D1B] font-semibold">Recurring blocked hours</p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-xs text-[#5C5A58] mb-1">Day</label>
                <select data-testid="recur-block-weekday" value={rbWeekday} onChange={(e) => setRbWeekday(Number(e.target.value))} className="rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-1.5 text-sm">
                  {WEEKDAYS_LONG.map((lbl, wd) => <option key={wd} value={wd}>{lbl}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#5C5A58] mb-1">From</label>
                <input data-testid="recur-block-start" type="time" step="900" value={rbStart} onChange={(e) => setRbStart(e.target.value)} className="rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-[#5C5A58] mb-1">To</label>
                <input data-testid="recur-block-end" type="time" step="900" value={rbEnd} onChange={(e) => setRbEnd(e.target.value)} className="rounded-[5px] border border-[#E9DDC6] bg-white px-2 py-1.5 text-sm" />
              </div>
              <Button data-testid="recur-block-add" onClick={addRecBlock} variant="outline" className="border-[#B08D57]/50 text-[#8A6D1B] hover:bg-[#B08D57]/10"><Plus size={15} weight="bold" className="mr-1" />Add</Button>
            </div>
            {recBlocks.length > 0 && (
              <div className="space-y-2">
                {recBlocks.map((b) => (
                  <div key={b.id} data-testid={`recur-block-row-${b.id}`} className="flex items-center justify-between rounded-[5px] bg-[#B08D57]/10 border border-[#B08D57]/25 px-3 py-2">
                    <span className="text-sm font-medium text-[#4A0000]">Every {WEEKDAYS_LONG[b.weekday]} · {fmtTime(b.start)} – {fmtTime(b.end)}</span>
                    <button data-testid={`remove-recur-block-${b.id}`} onClick={() => removeRecBlock(b.id)} className="text-[#8A6D1B] hover:text-[#7E1119] p-1"><Trash size={15} weight="bold" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid="weekly-schedule-cancel" disabled={recSaving} onClick={() => setRecurOpen(false)}>Cancel</Button>
            <Button data-testid="weekly-schedule-save" disabled={recSaving} onClick={saveRecurring} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{recSaving ? "Saving…" : "Save schedule"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
